import React from "react";
import { motion } from "framer-motion";
import { Activity, Shield, FileText, Crown, Radar, MessageSquare } from "lucide-react";

/**
 * AI Officers visual overlay for Political Intel.
 * Reframes existing capabilities (intel/brief/stakeholders/forecast) as
 * 6 specialized AI Officers — premium positioning for C-level demos.
 */

export const OFFICERS = [
  {
    id: "analyst",
    name: "AI Reputation Analyst",
    title: "Analista de Reputacion",
    icon: Activity,
    color: "from-purple-500 to-violet-500",
    accent: "purple",
    specialty: "Senales debiles · Patrones · Sentimiento",
    description: "Monitorea menciones reales, identifica patrones emergentes y mide la evolucion del sentimiento publico.",
    triggers: ["intel", "overview", "mentions"],
  },
  {
    id: "crisis",
    name: "AI Crisis Officer",
    title: "Oficial de Crisis",
    icon: Shield,
    color: "from-red-500 to-pink-500",
    accent: "red",
    specialty: "Clasificacion de riesgo · Protocolo · Mensajes iniciales",
    description: "Cuando detecta una senal de riesgo, clasifica nivel de amenaza y activa protocolo de respuesta.",
    triggers: ["risk", "crisis"],
  },
  {
    id: "executive",
    name: "AI Executive Advisor",
    title: "Asesor Ejecutivo",
    icon: Crown,
    color: "from-amber-500 to-orange-500",
    accent: "amber",
    specialty: "Brief CEO · Decisiones · Talking points",
    description: "Genera briefings nivel C-suite con recomendaciones de accion para DIRCOM y Junta Directiva.",
    triggers: ["brief"],
  },
  {
    id: "stakeholder",
    name: "AI Stakeholder Mapper",
    title: "Cartografo de Aliados",
    icon: Radar,
    color: "from-cyan-500 to-blue-500",
    accent: "cyan",
    specialty: "Aliados · Criticos · Influencia · Postura",
    description: "Extrae actores relevantes de las menciones reales y los clasifica por influencia y postura.",
    triggers: ["stakeholders"],
  },
  {
    id: "media",
    name: "AI Media Relations Officer",
    title: "Relaciones con Medios",
    icon: MessageSquare,
    color: "from-emerald-500 to-teal-500",
    accent: "emerald",
    specialty: "Pitch · Q&A · Comunicados · Mapa de medios",
    description: "Sugiere angulos noticiosos, prepara Q&A para prensa y mapea medios prioritarios.",
    triggers: ["media"],
  },
  {
    id: "forecast",
    name: "AI Forecast Officer",
    title: "Oficial de Proyeccion",
    icon: FileText,
    color: "from-pink-500 to-rose-500",
    accent: "pink",
    specialty: "Escenarios 30/60/90d · Drivers · Probabilidad",
    description: "Proyecta trayectoria reputacional bajo escenarios base, optimista y de riesgo.",
    triggers: ["forecast"],
  },
];

/**
 * Quick suggestion chips per officer.
 * Click = auto-send. Reduces friction before first message.
 */
export const OFFICER_SUGGESTIONS = {
  analyst: [
    "¿Cuáles son las 3 señales débiles más urgentes ahora mismo?",
    "Resume el sentimiento de la última semana en 2 frases",
    "¿Qué patrón emergente debería preocuparme?",
  ],
  crisis: [
    "Redacta un statement oficial para la crisis actual",
    "Dame un plan de respuesta para las próximas 48 horas",
    "¿Cuál es el nivel de amenaza y qué debo activar primero?",
  ],
  executive: [
    "Genera un brief ejecutivo de 5 bullets para el CEO",
    "¿Qué 3 decisiones críticas debo tomar esta semana?",
    "Dame talking points para junta directiva",
  ],
  stakeholder: [
    "¿Quiénes son mis aliados clave y cuál es su postura actual?",
    "Identifica los 3 críticos con más influencia",
    "Sugiere mensajes diferenciados por stakeholder",
  ],
  media: [
    "Sugiere 3 ángulos noticiosos para la próxima semana",
    "Prepara Q&A para preguntas difíciles de prensa",
    "¿Qué medios prioritarios debería contactar primero?",
  ],
  forecast: [
    "¿Cuál es el escenario más probable a 30 días?",
    "Identifica los 3 drivers principales de mi IRI",
    "¿Qué pasaría si el riesgo actual escala?",
  ],
};


export const OfficerBadge = ({ officer, active = false, size = "md" }) => {
  const Icon = officer.icon;
  const sizes = {
    sm: { box: "w-7 h-7", icon: "w-3.5 h-3.5" },
    md: { box: "w-10 h-10", icon: "w-5 h-5" },
    lg: { box: "w-14 h-14", icon: "w-6 h-6" },
  }[size];
  return (
    <div className={`${sizes.box} rounded-2xl bg-gradient-to-br ${officer.color} ${active ? "shadow-lg" : "opacity-90"} flex items-center justify-center flex-shrink-0`}>
      <Icon className={`${sizes.icon} text-white`} />
    </div>
  );
};


export const OfficerCard = ({ officer, status = "ready", onActivate }) => (
  <motion.button
    onClick={onActivate}
    whileHover={{ y: -2 }}
    className="text-left rounded-2xl bg-[#0A0A0F] border border-white/[0.08] hover:border-white/15 p-4 transition-all group"
    data-testid={`officer-card-${officer.id}`}
  >
    <div className="flex items-start gap-3 mb-3">
      <OfficerBadge officer={officer} active={status === "active"} />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">{officer.title}</p>
        <h3 className="text-sm font-bold text-white leading-tight">{officer.name}</h3>
      </div>
      <span className={`text-[9px] uppercase tracking-widest font-medium ${status === "active" ? "text-emerald-400" : "text-gray-500"}`}>
        {status === "active" ? "EN LINEA" : "LISTO"}
      </span>
    </div>
    <p className="text-[11px] text-gray-400 leading-relaxed">{officer.description}</p>
    <p className="text-[10px] text-purple-300 mt-2 font-medium">{officer.specialty}</p>
  </motion.button>
);


export const OfficersGrid = ({ activeId, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="officers-grid">
    {OFFICERS.map((o) => (
      <OfficerCard
        key={o.id}
        officer={o}
        status={activeId === o.id ? "active" : "ready"}
        onActivate={() => onSelect?.(o.id)}
      />
    ))}
  </div>
);


export const ActiveOfficerBanner = ({ officerId }) => {
  const officer = OFFICERS.find((o) => o.id === officerId);
  if (!officer) return null;
  return (
    <motion.div
      key={officerId}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] p-4 mb-4 flex items-center gap-4"
      data-testid={`officer-banner-${officerId}`}
    >
      <OfficerBadge officer={officer} active size="lg" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-medium">EN LINEA · IA en accion</span>
        </div>
        <h2 className="text-base font-bold text-white">{officer.name}</h2>
        <p className="text-xs text-gray-400 mt-0.5">{officer.specialty}</p>
      </div>
    </motion.div>
  );
};

export default OfficersGrid;
